// -----------------------------------------------------------------------------
// avatarColors — palette déterministe pour les avatars initiales.
//
// Objectif (spec 2026-02-XX rev7, Feb 2026) : chaque utilisateur doit avoir
// UNE couleur d'avatar unique et fixe qui reste identique dans tous les
// modules (Tâches, Documents, KPI drill-down, TopBar, LockScreen, …). Ainsi
// FZ reste toujours vert, HB toujours orange, etc. — utile pour scanner un
// tableau et identifier une personne d'un coup d'œil.
//
// Implémentation :
//   • Palette de 12 teintes choisies pour :
//       – rester harmonieuses sur fond dark (#1B1B1B),
//       – offrir un contraste WCAG ≥ 4.5:1 avec du texte blanc,
//       – être suffisamment distinctes entre elles.
//   • Hash 32-bit type FNV-1a sur la chaîne graine (userId, sinon email,
//     sinon nom) — stable entre renders, sessions et modules.
//   • Retour d'un objet { bg, fg, ring } (bg = fond plein, fg = couleur
//     texte à utiliser dessus, ring = version 22% opacity pour bordures /
//     halos éventuels).
// -----------------------------------------------------------------------------

// Palette validée sur fond #1B1B1B (12 couleurs équilibrées HSL espacées
// de ~30° + luminosité 55-65%). Chaque couleur inclut son ring semi-
// transparent pour les usages "halo" (ex: highlight assignés actifs).
const AVATAR_PALETTE = [
  { bg: "#E24B4A", fg: "#FFFFFF", ring: "rgba(226,75,74,0.28)"  }, // rouge
  { bg: "#EF9F27", fg: "#1B1B1B", ring: "rgba(239,159,39,0.28)" }, // orange
  { bg: "#F5C842", fg: "#1B1B1B", ring: "rgba(245,200,66,0.28)" }, // jaune
  { bg: "#97C459", fg: "#1B1B1B", ring: "rgba(151,196,89,0.28)" }, // vert lime
  { bg: "#3B6D11", fg: "#FFFFFF", ring: "rgba(59,109,17,0.28)"  }, // vert forêt
  { bg: "#5DCAA5", fg: "#1B1B1B", ring: "rgba(93,202,165,0.28)" }, // menthe
  { bg: "#3B9CE0", fg: "#FFFFFF", ring: "rgba(59,156,224,0.28)" }, // bleu ciel
  { bg: "#2B579A", fg: "#FFFFFF", ring: "rgba(43,87,154,0.28)"  }, // bleu Vertec
  { bg: "#8B5CF6", fg: "#FFFFFF", ring: "rgba(139,92,246,0.28)" }, // violet
  { bg: "#B983E8", fg: "#1B1B1B", ring: "rgba(185,131,232,0.28)"}, // mauve
  { bg: "#D4537E", fg: "#FFFFFF", ring: "rgba(212,83,126,0.28)" }, // rose
  { bg: "#D85A30", fg: "#FFFFFF", ring: "rgba(216,90,48,0.28)"  }, // brique
];

// FNV-1a 32-bit — hash rapide, déterministe, meilleure distribution qu'un
// simple multiplicateur *31 sur des UUIDs très proches (préfixe commun).
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h;
}

/**
 * avatarColorForSeed(seed) → { bg, fg, ring }
 *
 * Retourne toujours le MÊME triplet pour la même graine. Une graine vide
 * ou nulle retourne un gris neutre (safety net — ne devrait jamais arriver
 * en pratique côté UI puisque les avatars ont toujours un userId).
 */
export function avatarColorForSeed(seed) {
  const s = (seed || "").toString();
  if (!s) return { bg: "#3B3B3B", fg: "#EDEDED", ring: "rgba(59,59,59,0.28)" };
  const idx = fnv1a(s) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

/**
 * Raccourci quand on ne veut que le fond (usage courant : style={{ background }}).
 */
export function avatarBgForSeed(seed) {
  return avatarColorForSeed(seed).bg;
}
